// src/components/voucher/productScopePicker.jsx
// Product scope for a discount code (engine migration 0039).
//
// EMPTY SELECTION MEANS EVERY PRODUCT — that is the engine's rule, not a UI
// shortcut, so the closed button says "All products" rather than "None selected".
// Getting that label backwards would read as "this code is dead".
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

// The panel is PORTALLED TO document.body and positioned fixed, rather than
// absolutely inside the form.
//
// This theme puts `position: relative; z-index: 10` on every .card-body, so each
// card is its own stacking context. An in-flow panel is therefore trapped inside
// the "Create voucher" card no matter how high its own z-index goes, and the
// vouchers table — a later sibling at the same z-index — paints straight over the
// bottom of the product list and swallows its clicks. A portal is the only way
// out of a stacking context.
const PANEL_WIDTH = 280;
const PANEL_MAX_HEIGHT = 320;
const GAP = 4;

export default function ProductScopePicker({ products = [], value = [], onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  // Fixed positioning means the panel does not travel with the page, so its
  // anchor has to be re-measured on scroll and resize while it is open.
  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Flip above the trigger when there is not enough room below, so the list is
    // never half off-screen on a short viewport.
    const below = window.innerHeight - r.bottom - GAP;
    const flip = below < Math.min(PANEL_MAX_HEIGHT, 200) && r.top > below;
    setRect({
      left: Math.min(r.left, Math.max(0, window.innerWidth - PANEL_WIDTH - 8)),
      top: flip ? null : r.bottom + GAP,
      bottom: flip ? window.innerHeight - r.top + GAP : null,
      maxHeight: Math.max(120, Math.min(PANEL_MAX_HEIGHT, flip ? r.top - GAP * 2 : below)),
    });
  }, []);

  useLayoutEffect(() => { if (open) measure(); }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      // The panel is portalled, so it is NOT inside the trigger's subtree — it
      // needs its own containment check or every click in the list would close it.
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    const onReflow = () => measure();
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, measure]);

  // Drop the search text whenever the panel closes, so the next open starts on the
  // full catalogue. Without this the filter survives — pick "Coaster", create the
  // code, reopen for the next one and the list still shows a single product, which
  // reads as a broken picker rather than a stale filter.
  useEffect(() => { if (!open) setQ(""); }, [open]);

  const selected = useMemo(() => new Set(value), [value]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return products;
    return products.filter((p) => (p.name || "").toLowerCase().includes(needle));
  }, [products, q]);

  const selectedNames = useMemo(
    () => [...selected].map((id) => (products.find((p) => p.id === id) || {}).name).filter(Boolean),
    [selected, products],
  );

  const label = selected.size === 0
    ? "All products"
    : (selected.size === 1 ? (selectedNames[0] || "1 product") : `${selected.size} products`);

  const panel = open && rect ? (
    <div
      ref={panelRef}
      role="listbox"
      style={{
        position: "fixed",
        left: rect.left,
        ...(rect.top != null ? { top: rect.top } : { bottom: rect.bottom }),
        width: PANEL_WIDTH,
        maxHeight: rect.maxHeight,
        overflowY: "auto",
        padding: 8,
        // NOT className="card": this theme sets $card-bg to transparent (cards get
        // their edges from .card-arrow), so a .card floating panel renders
        // see-through and the page shows straight through the list.
        background: "var(--bs-component-bg)",
        border: "1px solid var(--bs-border-color)",
        borderRadius: 4,
        boxShadow: "0 6px 16px rgba(0,0,0,.35)",
        zIndex: 2000,
      }}
    >
      <input
        className="form-control form-control-sm mb-2"
        placeholder="Search products…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />
      <button
        type="button"
        className="btn btn-link btn-sm p-0 mb-2 text-decoration-none"
        onClick={() => onChange([])}
        disabled={selected.size === 0}
      >
        Clear — valid on all products
      </button>

      {filtered.map((p) => (
        <label key={p.id} className="form-check d-block mb-1" style={{ cursor: "pointer" }}>
          <input
            className="form-check-input"
            type="checkbox"
            checked={selected.has(p.id)}
            onChange={() => toggle(p.id)}
          />
          <span className="form-check-label ms-1">{p.name}</span>
        </label>
      ))}

      {products.length === 0 ? (
        <div className="text-inverse text-opacity-50 py-2" style={{ fontSize: 12 }}>
          No products loaded.
        </div>
      ) : null}
      {products.length > 0 && filtered.length === 0 ? (
        <div className="text-inverse text-opacity-50 py-2" style={{ fontSize: 12 }}>
          No product matches “{q}”.
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        className={`form-select text-start ${selected.size ? "border-theme" : ""}`}
        style={{ maxWidth: 240, minWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={selected.size ? selectedNames.join(", ") : "Valid on every product"}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </div>
  );
}
